/*
  assets/stamp-customizer.js
  Companion to snippets/stamp-customizer.liquid.
  - Validates required fields (including the design upload) before the product form submits.
  - Drag-and-drop file picker with type and size checks (Shopify accepts uploads up to 20 MB).
  - Drops empty optional fields so blank line item properties are not saved.
  - Draws a live preview of the stamp holder's engraved label as the customer types, in the chosen font.
  - When add-ons are selected, adds the stamp first (multipart, so uploads travel with it) and then
    each add-on with an "Add-on for" property, then re-uses the cart notification / drawer rendering
    that ships with the theme.
*/
if (!customElements.get('stamp-customizer')) {
  customElements.define(
    'stamp-customizer',
    class StampCustomizer extends HTMLElement {
      constructor() {
        super();
        this.onSubmit = this.onSubmit.bind(this);
      }

      connectedCallback() {
        this.form =
          document.getElementById(this.dataset.formId) ||
          document.querySelector(`#ProductInfo-${this.dataset.sectionId} form[action*="/cart/add"]`) ||
          document.querySelector('product-form form[action*="/cart/add"]');
        if (!this.form) {
          console.warn('[stamp-customizer] product form not found');
          return;
        }
        /* Use getAttribute: the form contains an input named "id", which shadows form.id. */
        this.formId = this.form.getAttribute('id');
        this.querySelectorAll('input, select, textarea').forEach((el) => {
          if (el.hasAttribute('data-addon')) return;
          if (el.getAttribute('form') !== this.formId) el.setAttribute('form', this.formId);
        });

        this.productForm = this.form.closest('product-form');
        this.submitButton = this.form.querySelector('[type="submit"]');
        this.spinner = this.submitButton ? this.submitButton.querySelector('.loading__spinner') : null;
        this.basePrice = parseInt(this.dataset.basePrice, 10) || 0;
        this.currency = this.dataset.currency || 'USD';
        this.addonsProperty = this.querySelector('[data-addons-property]');
        this.totalWrap = this.querySelector('[data-total-wrap]');
        this.totalEl = this.querySelector('[data-total]');
        this.addonInputs = Array.from(this.querySelectorAll('[data-addon]'));
        /* Free toggles in the add-ons list (special instructions): they reveal a field but are not cart lines. */
        this.toggleInputs = Array.from(this.querySelectorAll('[data-toggle]'));
        this.conditionals = Array.from(this.querySelectorAll('[data-conditional]'));

        this.addonInputs.forEach((input) => input.addEventListener('change', () => this.syncAddons()));
        this.toggleInputs.forEach((input) => input.addEventListener('change', () => this.syncAddons()));
        this.querySelectorAll('[data-field]:not([data-upload])').forEach((field) => {
          field.addEventListener('input', () => this.setFieldError(field, ''));
          field.addEventListener('change', () => this.setFieldError(field, ''));
        });

        this.initSelects();
        this.initDiscipline();
        this.initUploads();
        this.initEngraving();
        this.syncAddons();
        this.removeLegacyOptions();
        document.addEventListener('submit', this.onSubmit, true);
      }

      disconnectedCallback() {
        document.removeEventListener('submit', this.onSubmit, true);
        if (this.observer) this.observer.disconnect();
      }

      /* If the old BSS/Optis app is still assigned to this product, keep its duplicate fields out of the form. */
      removeLegacyOptions() {
        const purge = () =>
          this.form.querySelectorAll('.bss-po-option-set, .bss-po-product-page').forEach((node) => node.remove());
        purge();
        this.observer = new MutationObserver(purge);
        this.observer.observe(this.form, { childList: true, subtree: true });
        setTimeout(() => this.observer && this.observer.disconnect(), 15000);
      }

      /* Grey out dropdown text while the placeholder option is selected, like input placeholders. */
      initSelects() {
        this.querySelectorAll('select').forEach((select) => {
          const sync = () => select.classList.toggle('is-placeholder', select.value === '');
          select.addEventListener('change', sync);
          sync();
        });
      }

      /*
        Discipline "Other": a text box appears and what is typed becomes the value of the Other option,
        so the order (and the engraving default) carry the real discipline instead of the word "Other".
      */
      initDiscipline() {
        const select = this.querySelector('[name="properties[Discipline]"]');
        const input = this.querySelector('[data-discipline-other]');
        const other = select ? select.querySelector('option[data-other]') : null;
        if (!select || !input || !other) return;
        const sync = () => {
          const chosen = select.selectedOptions[0] === other;
          input.hidden = !chosen;
          other.value = (chosen && input.value.trim()) || 'Other';
        };
        select.addEventListener('change', (evt) => {
          sync();
          if (evt.isTrusted && !input.hidden) input.focus();
        });
        input.addEventListener('input', () => {
          sync();
          /* lets everything that follows the discipline (the engraving default) hear about it */
          select.dispatchEvent(new Event('change', { bubbles: true }));
        });
        sync();
      }

      /* ---------- file upload ---------- */

      initUploads() {
        this.uploads = Array.from(this.querySelectorAll('[data-upload]'));
        this.uploads.forEach((field) => {
          const input = field.querySelector('input[type="file"]');
          const zone = field.querySelector('[data-dropzone]');
          const clear = field.querySelector('[data-file-clear]');
          if (!input || !zone) return;

          input.addEventListener('change', () => {
            this.checkFile(field);
            this.renderUpload(field);
          });
          input.addEventListener('focus', () => zone.classList.add('is-focused'));
          input.addEventListener('blur', () => zone.classList.remove('is-focused'));

          ['dragenter', 'dragover'].forEach((type) =>
            zone.addEventListener(type, (evt) => {
              evt.preventDefault();
              zone.classList.add('is-dragover');
            })
          );
          ['dragleave', 'drop'].forEach((type) =>
            zone.addEventListener(type, (evt) => {
              evt.preventDefault();
              zone.classList.remove('is-dragover');
            })
          );
          zone.addEventListener('drop', (evt) => {
            const files = evt.dataTransfer && evt.dataTransfer.files;
            if (!files || !files.length) return;
            const transfer = new DataTransfer();
            transfer.items.add(files[0]);
            input.files = transfer.files;
            input.dispatchEvent(new Event('change', { bubbles: true }));
          });

          if (clear) {
            clear.addEventListener('click', () => {
              input.value = '';
              this.setFieldError(field, '');
              this.renderUpload(field);
            });
          }
          this.renderUpload(field);
        });
      }

      checkFile(field) {
        const input = field.querySelector('input[type="file"]');
        const file = input.files && input.files[0];
        if (!file) return true;

        const maxBytes = parseInt(input.dataset.maxBytes, 10) || 0;
        const accept = (input.getAttribute('accept') || '')
          .split(',')
          .map((token) => token.trim().toLowerCase())
          .filter(Boolean);
        const extension = '.' + file.name.split('.').pop().toLowerCase();
        const mime = (file.type || '').toLowerCase();
        const typeOk =
          !accept.length ||
          accept.some((token) => {
            if (token.startsWith('.')) return token === extension;
            if (token.endsWith('/*')) return mime.startsWith(token.slice(0, -1));
            return mime === token;
          });

        if (!typeOk) {
          input.value = '';
          this.setFieldError(field, `That file type is not supported. Please upload ${input.dataset.typesLabel || 'an image file'}.`);
          return false;
        }
        if (maxBytes && file.size > maxBytes) {
          input.value = '';
          this.setFieldError(
            field,
            `That file is too large (${this.formatBytes(file.size)}). The maximum size is ${this.formatBytes(maxBytes)}.`
          );
          return false;
        }
        this.setFieldError(field, '');
        return true;
      }

      renderUpload(field) {
        const input = field.querySelector('input[type="file"]');
        const file = input.files && input.files[0];
        const idle = field.querySelector('.stamp-customizer__dropzone-idle');
        const info = field.querySelector('.stamp-customizer__dropzone-file');
        const preview = field.querySelector('.stamp-customizer__dropzone-preview');
        const clear = field.querySelector('[data-file-clear]');
        const zone = field.querySelector('[data-dropzone]');

        if (preview && preview.dataset.objectUrl) {
          URL.revokeObjectURL(preview.dataset.objectUrl);
          delete preview.dataset.objectUrl;
          preview.removeAttribute('src');
          preview.hidden = true;
        }

        if (idle) idle.hidden = Boolean(file);
        if (info) info.hidden = !file;
        if (clear) clear.hidden = !file;
        if (zone) zone.classList.toggle('has-file', Boolean(file));
        if (!file) return;

        const name = field.querySelector('[data-file-name]');
        const size = field.querySelector('[data-file-size]');
        if (name) name.textContent = file.name;
        if (size) size.textContent = this.formatBytes(file.size);
        if (preview && /^image\/(png|jpeg|gif|webp|svg\+xml)$/.test(file.type)) {
          const url = URL.createObjectURL(file);
          preview.src = url;
          preview.dataset.objectUrl = url;
          preview.hidden = false;
        }
      }

      formatBytes(bytes) {
        if (bytes >= 1048576) return `${(bytes / 1048576).toFixed(1)} MB`;
        return `${Math.max(1, Math.round(bytes / 1024))} KB`;
      }

      /* ---------- add-ons ---------- */

      selectedAddons() {
        return this.addonInputs
          .filter((input) => input.checked && !input.disabled)
          .map((input) => ({
            key: input.dataset.addon,
            variantId: input.dataset.variantId,
            price: parseInt(input.dataset.price, 10) || 0,
            label: input.dataset.label || input.value,
          }));
      }

      syncAddons() {
        const selected = this.selectedAddons();
        const keys = selected
          .map((addon) => addon.key)
          .concat(this.toggleInputs.filter((input) => input.checked).map((input) => input.dataset.toggle));

        this.conditionals.forEach((wrap) => {
          const show = keys.includes(wrap.dataset.conditional);
          wrap.hidden = !show;
          wrap.querySelectorAll('input, select, textarea').forEach((el) => {
            el.disabled = !show;
          });
        });
        /* The label can only be measured while it is on screen, so draw it once its panel is open. */
        this.renderEngraving();

        if (this.addonsProperty) {
          this.addonsProperty.value = selected.map((addon) => addon.label).join(', ');
          this.addonsProperty.disabled = selected.length === 0;
        }

        const extra = selected.reduce((sum, addon) => sum + addon.price, 0);
        if (this.totalWrap) this.totalWrap.hidden = extra === 0;
        if (this.totalEl) this.totalEl.textContent = this.formatMoney(this.basePrice + extra);
      }

      /* ---------- engraving preview (stamp holder label) ---------- */

      /*
        The label is drawn the way the workshop file is built: a 61.48 x 26.59 mm curved label whose text
        sits on concentric arcs (centre 30.7403, -283.3966 mm; middle radius 298.2419; 6.8069 mm apart),
        11 pt bold, drawn exactly as typed (the defaults are in capitals). Every line is the same size: when the longest line is wider than the label,
        the whole label is made smaller until that line fits.
        Drawing units are tenths of a millimetre so browser minimum-font-size settings cannot distort it.
      */
      initEngraving() {
        this.engraving = this.querySelector('[data-engraving]');
        if (!this.engraving) return;
        const wrap = this.engraving;
        this.engravingLines = Array.from(wrap.querySelectorAll('[data-engraving-line]'));
        this.engravingFonts = Array.from(wrap.querySelectorAll('[data-engraving-font]'));
        this.engravingValue = wrap.querySelector('[data-engraving-value]');
        this.engravingSvg = wrap.querySelector('[data-engraving-svg]');
        this.engravingArcs = wrap.querySelector('[data-engraving-arcs]');
        this.engravingText = wrap.querySelector('[data-engraving-text]');
        this.engravingReset = wrap.querySelector('[data-engraving-reset]');
        /* Stands in on line 3 until the name is known; never accepted as the text to engrave. */
        this.engravingNamePlaceholder = 'YOUR NAME';

        /* A line follows the default (state, discipline / profession, name) until the customer types in it. */
        this.engravingLines.forEach((input) => {
          input.dataset.auto = 'true';
          input.addEventListener('input', () => {
            delete input.dataset.auto;
            this.renderEngraving();
          });
        });
        this.engravingFonts.forEach((radio) => radio.addEventListener('change', () => this.renderEngraving()));

        const add = wrap.querySelector('[data-engraving-add]');
        if (add) {
          add.addEventListener('click', () => {
            const row = wrap.querySelector('[data-engraving-row][hidden]');
            if (!row) return;
            row.hidden = false;
            add.hidden = true;
            row.querySelector('input').focus();
          });
        }
        if (this.engravingReset) {
          this.engravingReset.addEventListener('click', () => {
            this.engravingLines.forEach((input) => (input.dataset.auto = 'true'));
            this.applyEngravingSuggestion();
            this.renderEngraving();
          });
        }

        ['State', 'Profession', 'Discipline', 'Full name', 'Name'].forEach((name) => {
          const source = this.querySelector(`[name="properties[${name}]"]`);
          if (!source) return;
          const sync = () => {
            this.applyEngravingSuggestion();
            this.renderEngraving();
          };
          source.addEventListener('input', sync);
          source.addEventListener('change', sync);
        });

        /* Line widths depend on the font, so fit again whenever one finishes loading. */
        if (document.fonts && document.fonts.addEventListener) {
          document.fonts.addEventListener('loadingdone', () => this.renderEngraving());
        }
        this.applyEngravingSuggestion();
      }

      /*
        The first default, from the answers above: "STATE OF FLORIDA / CIVIL ENGINEER / YOUR NAME".
        Line 2 is the chosen engineering discipline, or the profession's title when there is none;
        line 3 becomes the customer's name once they have typed it. Null when the form has no such answers.
      */
      suggestedEngraving() {
        const type = this.dataset.formType;
        if (type !== 'pe' && type !== 'notary') return null;
        /* A switched-off field (the seal design has no such line, e.g. no discipline) does not count */
        const answer = (name) => {
          const el = this.querySelector(`[name="properties[${name}]"]`);
          return el && !el.disabled ? el.value.trim() : '';
        };
        const state = answer('State').replace(/\s*\(.*\)\s*$/, '');
        const territories = ['District of Columbia', 'Guam', 'Puerto Rico', 'Virgin Islands'];
        const commonwealths = ['Kentucky', 'Massachusetts', 'Pennsylvania', 'Virginia'];
        /* Canadian province products (snippets/stamp-customizer.liquid `provinces`) */
        const provinces = ['Alberta', 'British Columbia', 'Manitoba', 'Ontario', 'Quebec', 'Saskatchewan'];
        const inCanada = provinces.includes(state);
        let place = '';
        if (state) {
          /* Provinces: the name alone ("ALBERTA"), not "Province of ..." (owner, 2026-10-12) */
          if (inCanada || territories.includes(state)) place = state;
          else place = `${commonwealths.includes(state) ? 'Commonwealth' : 'State'} of ${state}`;
        }
        /* Engineers are "Professional"; every other profession is "Licensed". */
        const titles = {
          Engineer: 'Professional Engineer',
          Architect: 'Licensed Architect',
          'Landscape Architect': 'Licensed Landscape Architect',
          'Land Surveyor': 'Licensed Land Surveyor',
          Geologist: 'Licensed Geologist',
          'Interior Designer': 'Licensed Interior Designer',
        };
        /* The provinces' own titles, as their seals word them: P.Eng. / P.Geo.; Quebec engineers are "ingénieur"
           (Eng.), not P.Eng.; Ontario architects are licensed, the other provinces' registered. */
        if (inCanada) {
          Object.assign(titles, {
            Engineer: state === 'Quebec' ? 'Engineer' : 'Professional Engineer',
            Architect: state === 'Ontario' ? 'Licensed Architect' : 'Registered Architect',
            'Landscape Architect': 'Landscape Architect',
            'Land Surveyor': 'Land Surveyor',
            Geologist: 'Professional Geoscientist',
            'Interior Designer': 'Interior Designer',
          });
        }
        let title = type === 'notary' ? 'Notary Public' : this.dataset.engravingTitle || titles[answer('Profession')] || '';
        /* A typed discipline may already say "Engineering" or "Engineer" */
        const discipline = answer('Discipline').replace(/\s*engineer(ing)?\s*$/i, '');
        if (answer('Profession') === 'Engineer' && discipline && discipline !== 'Other') title = `${discipline} Engineer`;
        /* A seal design without a name line (e.g. a firm's evaluator number) has no name input: no name line then */
        const nameInput = this.querySelector(`[name="properties[${type === 'notary' ? 'Name' : 'Full name'}]"]`);
        const nameLine = nameInput && nameInput.disabled ? '' : answer(type === 'notary' ? 'Name' : 'Full name') || this.engravingNamePlaceholder;
        return [place, title, nameLine];
      }

      applyEngravingSuggestion() {
        const suggestion = this.suggestedEngraving();
        if (!suggestion || !this.engravingLines) return;
        this.engravingLines.forEach((input, index) => {
          if (input.dataset.auto === 'true') input.value = (suggestion[index] || '').toUpperCase();
        });
      }

      renderEngraving() {
        if (!this.engraving) return;
        const entries = this.engravingLines
          .map((input, index) => ({ row: index + 1, text: input.value.trim() }))
          .filter((entry) => entry.text);
        if (this.engravingValue) this.engravingValue.value = entries.map((entry) => entry.text).join('\n');
        if (this.engravingReset) {
          this.engravingReset.hidden =
            !this.suggestedEngraving() || this.engravingLines.every((input) => input.dataset.auto === 'true');
        }
        if (this.engraving.hidden || !this.engravingSvg) return;

        const SVG_NS = 'http://www.w3.org/2000/svg';
        const UNIT = 10;
        const CENTER_X = 30.7403 * UNIT;
        const CENTER_Y = -283.3966 * UNIT;
        const MIDDLE_RADIUS = 298.2419 * UNIT;
        const HALF_CHORD = 46 * UNIT;
        const LABEL_WIDTH = 61.4803 * UNIT;

        const empty = entries.length === 0;
        const shown = empty ? [{ text: 'YOUR ENGRAVING' }, { text: 'APPEARS HERE' }] : entries;
        let layout = { pitch: 6.8069, size: 3.88056 };
        if (shown.length === 4) layout = { pitch: 5.3, size: 3.3 };
        if (shown.length > 4) layout = { pitch: 4.3, size: 2.8 };

        const checked = this.engravingFonts.find((radio) => radio.checked);
        const arial = Boolean(checked) && checked.value === 'Arial';
        const family = arial
          ? "'KW Label Arial', Arial, Arimo, 'Liberation Sans', Helvetica, sans-serif"
          : "'KW Label Times', 'Times New Roman', Tinos, 'Liberation Serif', Times, serif";
        const prefix = this.engraving.dataset.arcPrefix || 'StampCustomizerArc-';

        this.engravingArcs.textContent = '';
        this.engravingText.textContent = '';
        this.engravingText.setAttribute('class', empty ? 'is-sample' : '');
        const reduced = [];

        shown.forEach((entry, index) => {
          const radius = MIDDLE_RADIUS + (index - (shown.length - 1) / 2) * layout.pitch * UNIT;
          const baselineY = CENTER_Y + Math.sqrt(radius * radius - HALF_CHORD * HALF_CHORD);
          const arc = document.createElementNS(SVG_NS, 'path');
          arc.setAttribute('id', `${prefix}${index + 1}`);
          arc.setAttribute(
            'd',
            `M ${CENTER_X - HALF_CHORD} ${baselineY} A ${radius} ${radius} 0 0 0 ${CENTER_X + HALF_CHORD} ${baselineY}`
          );
          this.engravingArcs.appendChild(arc);

          const text = document.createElementNS(SVG_NS, 'text');
          text.setAttribute('font-size', layout.size * UNIT);
          text.setAttribute('font-weight', '700');
          text.setAttribute('text-anchor', 'middle');
          text.style.fontFamily = family;
          const onArc = document.createElementNS(SVG_NS, 'textPath');
          onArc.setAttribute('href', `#${prefix}${index + 1}`);
          onArc.setAttribute('startOffset', '50%');
          onArc.textContent = entry.text;
          text.appendChild(onArc);
          this.engravingText.appendChild(text);

          /* Width left between the slanted side edges at this baseline, less a 2.6 mm margin each side. */
          const depth = Math.min(1, Math.max(0, ((CENTER_Y + radius) / UNIT - 1.85) / (22.99 - 1.85)));
          const usable = LABEL_WIDTH - 2 * (2.18 + (0.06 - 2.18) * depth + 2.6) * UNIT;
          let length = 0;
          try {
            length = text.getComputedTextLength();
          } catch (error) {
            length = 0;
          }
          if (length > usable) reduced.push({ row: entry.row, scale: usable / length });
        });

        /* One size for the whole label: when the longest line has to shrink, every line shrinks with it. */
        const labelScale = reduced.reduce((min, item) => Math.min(min, item.scale), 1);
        if (labelScale < 1) {
          this.engravingText
            .querySelectorAll('text')
            .forEach((text) => text.setAttribute('font-size', layout.size * UNIT * labelScale));
        }

        this.engravingSvg.setAttribute(
          'aria-label',
          empty ? 'Preview of your engraved label' : `Preview of your engraved label: ${entries.map((entry) => entry.text).join(', ')}`
        );

      }

      formatMoney(cents) {
        try {
          return new Intl.NumberFormat(document.documentElement.lang || 'en', {
            style: 'currency',
            currency: this.currency,
          }).format(cents / 100);
        } catch (e) {
          return (cents / 100).toFixed(2);
        }
      }

      /* ---------- validation ---------- */

      setFieldError(field, message) {
        const error = field.querySelector('.stamp-customizer__error');
        field.classList.toggle('stamp-customizer__field--error', Boolean(message));
        field.querySelectorAll('input, select, textarea').forEach((el) => {
          if (message) el.setAttribute('aria-invalid', 'true');
          else el.removeAttribute('aria-invalid');
        });
        if (error) {
          error.textContent = message || '';
          error.hidden = !message;
        }
      }

      validate() {
        let firstInvalid = null;
        this.querySelectorAll('[data-required]').forEach((field) => {
          if (field.hidden || field.closest('[hidden]')) return;
          const label = field.dataset.field || 'This field';
          const radios = field.querySelectorAll('input[type="radio"]');
          const fileInput = field.querySelector('input[type="file"]');
          let valid;
          let message = `${label} is required.`;
          if (field.hasAttribute('data-engraving')) {
            /* The holder product: at least one line of engraving text (its first inputs are the font radios) */
            valid = Boolean(this.engravingValue && this.engravingValue.value.trim());
            message = 'Please enter the text to engrave on your holder.';
          } else if (fileInput) {
            valid = Boolean(fileInput.files && fileInput.files.length);
            message = 'Please upload your design file.';
          } else if (radios.length) {
            valid = Array.from(radios).some((radio) => radio.checked);
          } else {
            const control = field.querySelector('input, select, textarea');
            /* A license field that still holds only the label kw-seal-preview.js started it with ("Lic. No. ") is empty */
            const prefill = control && control.dataset.kwPrefill ? control.dataset.kwPrefill.trim() : '';
            valid = Boolean(control) && control.value.trim() !== '' && control.value.trim() !== prefill;
          }
          this.setFieldError(field, valid ? '' : message);
          if (!valid && !firstInvalid) firstInvalid = field;
        });

        /* The "YOUR NAME" stand-in must not reach the engraver. */
        let staleLine = null;
        const engravingMissing =
          this.engraving && this.engraving.hasAttribute('data-required') && !(this.engravingValue && this.engravingValue.value.trim());
        /* (skipped while the "text is required" message above is showing, so it is not wiped) */
        if (this.engraving && !this.engraving.hidden && !engravingMissing) {
          staleLine = this.engravingLines.find(
            (input) => input.value.trim().toUpperCase() === this.engravingNamePlaceholder
          );
          this.setFieldError(
            this.engraving,
            staleLine ? `Please replace "${this.engravingNamePlaceholder}" with the name to engrave.` : ''
          );
          if (staleLine && !firstInvalid) firstInvalid = staleLine.closest('[data-engraving-row]');
        }

        if (firstInvalid) {
          firstInvalid.scrollIntoView({ behavior: 'smooth', block: 'center' });
          const focusable = firstInvalid.querySelector('input:not([type="hidden"]), select, textarea');
          if (focusable) focusable.focus({ preventScroll: true });
          return false;
        }
        return true;
      }

      /* Temporarily disable empty optional inputs so they are left out of the submission. Returns a restore function. */
      pruneEmpty() {
        const pruned = [];
        this.querySelectorAll('[name^="properties["]').forEach((el) => {
          if (el.disabled || ['radio', 'checkbox', 'hidden', 'file'].includes(el.type)) return;
          if (el.value.trim() === '') {
            el.disabled = true;
            pruned.push(el);
          }
        });
        /* A font choice means nothing without engraving text. */
        if (this.engravingValue && this.engravingValue.value.trim() === '') {
          this.engravingFonts.forEach((radio) => {
            if (radio.disabled) return;
            radio.disabled = true;
            pruned.push(radio);
          });
        }
        return () => pruned.forEach((el) => (el.disabled = false));
      }

      /* ---------- submission ---------- */

      onSubmit(evt) {
        if (evt.target !== this.form) return;

        if (!this.validate()) {
          evt.preventDefault();
          evt.stopImmediatePropagation();
          return;
        }

        const restore = this.pruneEmpty();
        const addons = this.selectedAddons();
        if (!addons.length) {
          /* No add-ons: product-form.js handles the submission as usual (multipart, uploads included). */
          setTimeout(restore, 0);
          return;
        }

        evt.preventDefault();
        evt.stopImmediatePropagation();
        this.addToCart(addons).finally(restore);
      }

      requestConfig(type) {
        const config =
          typeof fetchConfig === 'function'
            ? fetchConfig('javascript')
            : { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' } };
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        if (type === 'multipart') delete config.headers['Content-Type'];
        return config;
      }

      async addToCart(addons) {
        const button = this.submitButton;
        if (button && button.getAttribute('aria-disabled') === 'true') return;
        if (this.productForm && this.productForm.handleErrorMessage) this.productForm.handleErrorMessage();
        if (button) {
          button.setAttribute('aria-disabled', true);
          button.classList.add('loading');
        }
        if (this.spinner) this.spinner.classList.remove('hidden');

        const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        const sections = cart ? cart.getSectionsToRender().map((section) => section.id).join(',') : null;
        const addUrl = (window.routes && window.routes.cart_add_url) || '/cart/add';

        const formData = new FormData(this.form);
        const properties = {};
        formData.forEach((value, key) => {
          const match = key.match(/^properties\[(.+)\]$/);
          if (match && typeof value === 'string' && value.trim() !== '') properties[match[1]] = value;
        });
        const quantity = Math.max(1, parseInt(formData.get('quantity') || '1', 10) || 1);
        const mainId = formData.get('id');
        if (cart) {
          formData.append('sections', sections);
          formData.append('sections_url', window.location.pathname);
          cart.setActiveElement(document.activeElement);
        }

        const owner = [this.dataset.productTitle, properties['Full name']].filter(Boolean).join(' - ');
        const addonItems = addons.map((addon) => {
          const addonProperties = { 'Add-on for': owner };
          if (addon.key === 'holder' && properties['Custom Engraving']) {
            addonProperties['Custom Engraving'] = properties['Custom Engraving'];
            if (properties['Engraving font']) addonProperties['Engraving font'] = properties['Engraving font'];
          }
          return { id: addon.variantId, quantity, properties: addonProperties };
        });

        const fail = (json) => {
          if (window.publish && window.PUB_SUB_EVENTS) {
            publish(PUB_SUB_EVENTS.cartError, {
              source: 'product-form',
              productVariantId: mainId,
              errors: json.errors || json.description,
              message: json.message,
            });
          }
          if (this.productForm && this.productForm.handleErrorMessage) {
            this.productForm.handleErrorMessage(json.description || json.message);
          }
        };

        try {
          /* Step 1: the stamp itself, as multipart so a design upload is included. */
          const mainConfig = this.requestConfig('multipart');
          mainConfig.body = formData;
          const mainJson = await (await fetch(addUrl, mainConfig)).json();
          if (mainJson.status) {
            fail(mainJson);
            return;
          }

          /* Step 2: the add-ons, linked to the stamp. */
          const addonConfig = this.requestConfig('json');
          const addonBody = { items: addonItems };
          if (cart) {
            addonBody.sections = sections;
            addonBody.sections_url = window.location.pathname;
          }
          addonConfig.body = JSON.stringify(addonBody);
          const addonJson = await (await fetch(addUrl, addonConfig)).json();

          if (!cart) {
            window.location = (window.routes && window.routes.cart_url) || '/cart';
            return;
          }

          /* The cart notification highlights the line whose key is on the response. */
          const result = addonJson.status ? mainJson : addonJson;
          result.key = mainJson.key;
          result.id = mainJson.id;
          if (window.publish && window.PUB_SUB_EVENTS) {
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'product-form', productVariantId: mainId, cartData: result });
          }
          cart.renderContents(result);
          if (addonJson.status) {
            fail({ description: `Your stamp was added, but an add-on could not be: ${addonJson.description || addonJson.message}` });
          }
        } catch (error) {
          console.error('[stamp-customizer]', error);
        } finally {
          if (button) {
            button.classList.remove('loading');
            button.removeAttribute('aria-disabled');
          }
          if (cart && cart.classList.contains('is-empty')) cart.classList.remove('is-empty');
          if (this.spinner) this.spinner.classList.add('hidden');
        }
      }
    }
  );
}
